import { Action, ActionType, createAsyncAction, createReducer } from 'typesafe-actions';
import { ofType } from 'redux-observable';
import { Observable, OperatorFunction, from } from 'rxjs';
import { switchMap, withLatestFrom, catchError, map } from 'rxjs/operators';
import { produce } from 'immer';
import { toReduxError, type ReduxError } from '@shared/errors';
import { apiClient, ApiType, ApiPaths } from '@shared/apiClient';
import { TypedEpic } from '../types';
import { ArticleListEditor, ArticleResponse, ArticleType, Category } from '../../../../types/articleType';
import { SiteStructureType } from '../../../../types/siteStructureType';

// Actions
export enum Actions {
  GET_ARTICLE_LIST = 'GET_ARTICLE_LIST',
  GET_ARTICLE_LIST_SUCCESS = 'GET_ARTICLE_LIST_SUCCESS',
  GET_ARTICLE_LIST_FAIL = 'GET_ARTICLE_LIST_FAIL',
  GET_ARTICLE_LIST_CANCEL = 'GET_ARTICLE_LIST_CANCEL',
  GET_MORE_ARTICLES = 'GET_MORE_ARTICLES',
  GET_MORE_ARTICLES_SUCCESS = 'GET_MORE_ARTICLES_SUCCESS',
  GET_MORE_ARTICLES_FAIL = 'GET_MORE_ARTICLES_FAIL',
  GET_MORE_ARTICLES_CANCEL = 'GET_MORE_ARTICLES_CANCEL',
}

const allArticlesKey = 'all-articles';

/** Articles per request. The epics define the pagination contract — the skeleton count must match it. */
export const ARTICLE_PAGE_SIZE = 9;

export type ArticleListRequest = {
  category: SiteStructureType | null;
  authorSlug?: string;
} | null;

type ArticleListMeta = {
  category?: SiteStructureType;
  authorSlug?: string;
};

export const getArticleListKey = (request?: ArticleListRequest | ArticleListMeta) => {
  const baseKey = request?.category?.slug || allArticlesKey;
  return request?.authorSlug ? `${baseKey}-author-${request.authorSlug}` : baseKey;
};

export interface State {
  data: Record<
    string,
    {
      pages: Record<number, { isLoading: boolean; data?: Record<ArticleType['slug'], ArticleType> }>;
      total: number;
      totalFetchedArticles: number;
      isLoadingMore: boolean;
      numberOfLoadedPages: number;
      categories?: Category[];
      editors?: ArticleListEditor[];
      isUpdating: boolean;
      errorState?: ReduxError;
    }
  >;
}

export const initialState: State = {
  data: {},
};

export const actions = {
  getArticleList: createAsyncAction(
    Actions.GET_ARTICLE_LIST, // request payload creator
    Actions.GET_ARTICLE_LIST_SUCCESS, // success payload creator
    Actions.GET_ARTICLE_LIST_FAIL, // failure payload creator
    Actions.GET_ARTICLE_LIST_CANCEL, // optional cancel payload creator
  )<ArticleListRequest, [ArticleResponse, ArticleListMeta], [ReduxError, ArticleListMeta], ArticleListRequest>(),
  getMoreArticles: createAsyncAction(
    Actions.GET_MORE_ARTICLES, // request payload creator
    Actions.GET_MORE_ARTICLES_SUCCESS, // success payload creator
    Actions.GET_MORE_ARTICLES_FAIL, // failure payload creator
    Actions.GET_MORE_ARTICLES_CANCEL, // optional cancel payload creator
  )<ArticleListRequest, [ArticleResponse, ArticleListMeta], [ReduxError, ArticleListMeta], ArticleListRequest>(),
};

export const reducers = createReducer<State, Action>(initialState, {})
  .handleAction(actions.getArticleList.request, (state = initialState, action) =>
    produce(state, (draftState) => {
      const key = getArticleListKey(action.payload);
      draftState.data[key] = {
        pages: { 1: { data: null, isLoading: true } },
        isLoadingMore: true,
        numberOfLoadedPages: 0,
        totalFetchedArticles: 0,
        total: 0,
        isUpdating: false,
      };
    }),
  )
  .handleAction(actions.getArticleList.success, (state = initialState, action) =>
    produce(state, (draftState) => {
      const key = getArticleListKey(action.meta);
      draftState.data[key].isUpdating = false;
      draftState.data[key].total = action.payload.total;
      draftState.data[key].totalFetchedArticles += action.payload.items.length;
      draftState.data[key].categories = action.payload.categories;
      draftState.data[key].editors = action.payload.editors;
      draftState.data[key].isLoadingMore = false;
      draftState.data[key].numberOfLoadedPages = 1;
      draftState.data[key].pages = {
        1: {
          data: action.payload.items.reduce((acc: Record<ArticleType['slug'], ArticleType>, cur) => {
            acc[cur.slug] = { ...cur, stateMeta: { isEnriched: false, fetchedAt: new Date().toISOString() } };
            return acc;
          }, {}),
          isLoading: false,
        },
      };
    }),
  )
  .handleAction(actions.getArticleList.failure, (state = initialState, action) =>
    produce(state, (draftState) => {
      const key = getArticleListKey(action.meta);
      draftState.data[key].isUpdating = false;
      delete draftState.data[key].pages[1];
      draftState.data[key].errorState = action.payload;
    }),
  )
  .handleAction(actions.getArticleList.cancel, (state = initialState, action) =>
    produce(state, (draftState) => {
      const key = getArticleListKey(action.payload);
      delete draftState.data[key].pages[1];
      draftState.data[key].isUpdating = false;
    }),
  )
  .handleAction(actions.getMoreArticles.request, (state = initialState, action) =>
    produce(state, (draftState) => {
      const key = getArticleListKey(action.payload);
      draftState.data[key].isLoadingMore = true;
      draftState.data[key].pages[state.data[key].numberOfLoadedPages + 1] = {
        data: null,
        isLoading: true,
      };
    }),
  )
  .handleAction(actions.getMoreArticles.success, (state = initialState, action) =>
    produce(state, (draftState) => {
      const key = getArticleListKey(action.meta);
      draftState.data[key].isLoadingMore = false;
      draftState.data[key].totalFetchedArticles += action.payload.items.length;
      if (action.payload.editors) {
        draftState.data[key].editors = action.payload.editors;
      }
      draftState.data[key].pages[state.data[key].numberOfLoadedPages + 1] = {
        isLoading: false,
        data: action.payload.items.reduce((acc: Record<ArticleType['slug'], ArticleType>, cur) => {
          acc[cur.slug] = { ...cur, stateMeta: { isEnriched: false, fetchedAt: new Date().toISOString() } };
          return acc;
        }, {}),
      };
      draftState.data[key].numberOfLoadedPages += 1;
    }),
  )
  .handleAction(actions.getMoreArticles.failure, (state = initialState, action) =>
    produce(state, (draftState) => {
      const key = getArticleListKey(action.meta);
      draftState.data[key].isLoadingMore = false;
      delete draftState.data[key].pages[state.data[key].numberOfLoadedPages + 1];
      draftState.data[key].errorState = action.payload;
    }),
  )
  .handleAction(actions.getMoreArticles.cancel, (state = initialState, action) =>
    produce(state, (draftState) => {
      const key = getArticleListKey(action.payload);
      draftState.data[key].isLoadingMore = false;
      delete draftState.data[key].pages[draftState.data[key].numberOfLoadedPages + 1];
    }),
  );

const fetchArticleListEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_ARTICLE_LIST),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.getArticleList.request>[]
    >,
    switchMap(([action]: { payload: ArticleListRequest }[]) => {
      const params: Record<string, any> = {
        orderBy: 'published',
        take: ARTICLE_PAGE_SIZE,
      };

      if (action.payload?.category) {
        params.categories = action.payload.category.slug;
      } else {
        params.includeCategories = false;
      }

      if (action.payload?.authorSlug) {
        params.editorSlugs = action.payload.authorSlug;
      }

      return from(
        apiClient.get<ArticleResponse>(ApiType.apimNafNoApi, {
          url: ApiPaths[ApiType.apimNafNoApi].articles.list,
          params,
        }),
      ).pipe(
        map((response) =>
          actions.getArticleList.success(response.data, {
            category: action.payload?.category,
            authorSlug: action.payload?.authorSlug,
          }),
        ),
        catchError((err) =>
          from([
            actions.getArticleList.failure(toReduxError(err, 'Could not get articles'), {
              category: action.payload?.category,
              authorSlug: action.payload?.authorSlug,
            }),
          ]),
        ),
      );
    }),
  );

const fetchMoreArticlesEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_MORE_ARTICLES),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.getMoreArticles.request>[]
    >,
    switchMap(([action]: { payload: ArticleListRequest }[]) => {
      const currentState = state$.value.articles;
      const key = getArticleListKey(action.payload);
      const currentData = currentState.data[key];
      const nextPage = currentData.numberOfLoadedPages + 1;

      const params: Record<string, any> = {
        orderBy: 'published',
        take: ARTICLE_PAGE_SIZE,
        skip: (nextPage - 1) * ARTICLE_PAGE_SIZE,
      };

      if (action.payload?.category) {
        params.categories = action.payload.category.slug;
      } else {
        params.includeCategories = false;
      }

      if (action.payload?.authorSlug) {
        params.editorSlugs = action.payload.authorSlug;
      }

      return from(
        apiClient.get<ArticleResponse>(ApiType.apimNafNoApi, {
          url: ApiPaths[ApiType.apimNafNoApi].articles.list,
          params,
        }),
      ).pipe(
        map((response) =>
          actions.getMoreArticles.success(response.data, {
            category: action.payload?.category,
            authorSlug: action.payload?.authorSlug,
          }),
        ),
        catchError((err) =>
          from([
            actions.getMoreArticles.failure(toReduxError(err, 'Could not get more articles'), {
              category: action.payload?.category,
              authorSlug: action.payload?.authorSlug,
            }),
          ]),
        ),
      );
    }),
  );

export const epics: TypedEpic[] = [fetchMoreArticlesEpic, fetchArticleListEpic];
